import React, { useMemo, memo } from 'react';

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  
}

const ROWS_PER_PAGE = 10;

const Pagination: React.FC<PaginationProps> = memo(({ currentPage, totalItems, onPageChange }) => {
  
  const totalPages = Math.ceil(totalItems / ROWS_PER_PAGE);

  const generatePageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const paginatedPageNumbers = useMemo(() => generatePageNumbers(), [currentPage, totalPages]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  return (
    <div className="flex items-center justify-center gap-4 mt-4">
      
      <div>
        <ul className="flex items-center justify-center my-6 gap-3 text-sm">
          <li>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center justify-center w-7 h-7 leading-tight text-primaryblue-50 border border-primaryblue-50 rounded-md disabled:opacity-50"
              aria-label="Previous page"
            >
              <span className="sr-only">Previous</span>
              <i className="st-arrow-right text-xl font-semibold rotate-180"></i>
            </button>
          </li>
          {paginatedPageNumbers.map((page) => (
            <li key={page}>
              <button
                onClick={() => handlePageChange(page)}
                className={`flex items-center justify-center w-7 h-7 rounded-md border border-primaryblue-50 ${
                  currentPage === page ? 'text-white bg-primaryblue-50' : 'text-primaryblue-50'
                }`}
                aria-label={`Page ${page}`}
              >
                {page}
              </button>
            </li>
          ))}
          <li>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center justify-center w-7 h-7 text-primaryblue-50 border border-primaryblue-50 rounded-md disabled:opacity-50"
              aria-label="Next page"
            >
              <span className="sr-only">Next</span>
              <i className="st-arrow-right text-xl font-semibold"></i>
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
});

Pagination.displayName = "Pagination";

export default Pagination;